import React, { FC, Fragment } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { Grid } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';
import { find } from 'lodash';

import { LeaderboardConfig, LeaderboardForUser } from 'api';
import { EmptyState, Layouts, LoadMoreButton, NotFound, Queries } from 'components';

import LeaderboardSelect from '../components/Select';
import LeaderboardHeader from '../components/Header';
import LeaderboardTable from '../components/Table';
import LeaderboardLoader from '../components/Loader';

interface IMatchParams {
  leaderboard_id: string;
}

interface IPropsEnhanced extends RouteComponentProps<IMatchParams>, WithStyles<typeof styles> {}

const styles = (theme: Theme) =>
  createStyles({
    main: {
      marginBottom: theme.spacing(4),
    },
  });

const enhancers = compose<IPropsEnhanced, {}>(withRouter, withStyles(styles));

const LeaderboardIndex: FC<IPropsEnhanced> = ({ classes, history, match }) => {
  const { params } = match;

  function handleCompleted(data: { getLeaderboardForUser: LeaderboardForUser }) {
    if (params.leaderboard_id) {
      return;
    }

    const { getLeaderboardForUser } = data;
    const { leaderboard_configs } = getLeaderboardForUser;
    const [firstConfig] = leaderboard_configs;

    history.replace(`/leaderboard/${firstConfig.id}`);
  }

  function getCurrentConfig(leaderboardConfigs: LeaderboardConfig[]) {
    if (params.leaderboard_id) {
      return find(leaderboardConfigs, ['id', params.leaderboard_id]);
    }

    const [firstConfig] = leaderboardConfigs;
    return firstConfig;
  }

  function renderEmptyState() {
    return <Layouts.App1Column main={<EmptyState name="leaderboard" />} />;
  }

  function renderMain(config: LeaderboardConfig) {
    return (
      <div className={classes.main}>
        <Grid container direction="column" wrap="nowrap" spacing={2}>
          <Grid item>
            <LeaderboardHeader config={config} />
          </Grid>
          <Grid item>
            <Queries.GetUserLeaderboard
              loader={<LeaderboardLoader />}
              variables={{ leaderboard_config_id: config.id }}>
              {(data, { onLoadMore, loading, exhausted }) => {
                const { leaderboard_scores, user_score } = data;
                const { items } = leaderboard_scores;

                return (
                  <Fragment>
                    <LeaderboardTable items={items} userScore={user_score} />
                    {!exhausted && <LoadMoreButton loading={loading} onClick={onLoadMore} />}
                  </Fragment>
                );
              }}
            </Queries.GetUserLeaderboard>
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <Queries.GetUserLeaderboards fetchPolicy="network-only" onCompleted={handleCompleted}>
      {data => {
        const { leaderboard_configs } = data;
        if (!leaderboard_configs.length) {
          return renderEmptyState();
        }

        const currentConfig = getCurrentConfig(leaderboard_configs);
        if (!currentConfig) {
          return <NotFound />;
        }

        return (
          <Layouts.App2Column
            desktopSplit="3/9"
            sidebar={<LeaderboardSelect configs={leaderboard_configs} />}
            main={renderMain(currentConfig)}
          />
        );
      }}
    </Queries.GetUserLeaderboards>
  );
};

export default enhancers(LeaderboardIndex);
